import { useEffect, useState } from 'react';
import { ChevronLeft, Star, Repeat } from 'lucide-react';
import * as SocialAPI from '../services/SocialAPI';
import { ensureCardsLoaded, getCardInfo } from '../utils/cardIndex';
import { getVariantConfig } from '../utils/cardVariants';

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMzIiIHI9IjEwIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';

const PRIORITY_STYLES = {
  high: 'bg-danger-bg border-danger-border text-brick',
  medium: 'bg-yellow/15 border-yellow/40 text-yellow-dark',
  low: 'bg-cream-bg border-border text-ink-secondary'
};

const initials = (username) => (username ? username.slice(0, 2).toUpperCase() : '?');

// A friend's public trade surface: their wishlist (snapshot data) and their
// tradeable duplicates (hydrated via cardIndex).
const FriendDetailView = ({ friend, onBack }) => {
  const [wishlist, setWishlist] = useState(null);
  const [tradeables, setTradeables] = useState(null);
  const [error, setError] = useState('');
  const [, setHydrated] = useState(0);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      SocialAPI.getFriendWishlist(friend.userId),
      SocialAPI.getFriendTradeables(friend.userId)
    ])
      .then(async ([wl, tr]) => {
        if (cancelled) return;
        setWishlist(wl);
        setTradeables(tr);
        await ensureCardsLoaded(tr.map((t) => t.cardId));
        if (!cancelled) setHydrated((h) => h + 1);
      })
      .catch((err) => {
        console.error('Failed to load friend detail:', err);
        if (!cancelled) setError("Couldn't load this friend's collection data.");
      });

    return () => {
      cancelled = true;
    };
  }, [friend.userId]);

  const loading = wishlist === null || tradeables === null;

  return (
    <div className="px-4 md:px-10 py-6 max-w-3xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-ink-secondary hover:text-ink-primary text-sm mb-4"
      >
        <ChevronLeft size={16} />
        Back to friends
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow to-brick flex items-center justify-center text-navy-dark text-base font-heading font-bold">
          {initials(friend.username)}
        </div>
        <div>
          <h2 className="font-heading font-bold text-xl text-ink-primary">{friend.username}</h2>
          {friend.since && (
            <p className="text-[12px] text-ink-secondary">
              Friends since {new Date(friend.since).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2.5 bg-danger-bg border border-danger-border rounded-xl text-brick text-[13px]">
          {error}
        </div>
      )}

      {loading && !error ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-navy-dark border-t-transparent"></div>
        </div>
      ) : !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Their tradeables */}
          <div>
            <h3 className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
              <Repeat size={12} />
              For trade ({tradeables.length})
            </h3>
            {tradeables.length === 0 ? (
              <p className="text-[13px] text-ink-secondary bg-white border border-border rounded-xl p-4">
                No duplicates offered right now.
              </p>
            ) : (
              <div className="space-y-2">
                {tradeables.map((t) => {
                  const info = getCardInfo(t.cardId);
                  const config = getVariantConfig(t.variant);
                  return (
                    <div
                      key={`${t.cardId}|${t.variant}`}
                      className="bg-white border border-border rounded-xl p-2.5 flex items-center gap-2.5"
                    >
                      <img
                        src={info?.image || FALLBACK_IMG}
                        alt={info?.name || t.cardId}
                        className="w-9 h-12 object-contain rounded flex-shrink-0"
                        onError={(e) => {
                          e.target.src = FALLBACK_IMG;
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold text-ink-primary truncate">
                          {info?.name || t.cardId}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={`${config.color} text-white text-[8px] font-bold rounded px-1 py-0.5`}>
                            {config.short}
                          </span>
                          <span className="text-[10px] text-ink-secondary truncate">
                            {info ? `${info.setName} #${info.number}` : t.variant}
                          </span>
                        </div>
                      </div>
                      <span className="text-[11px] font-bold text-success-green flex-shrink-0">
                        ×{t.tradeableQty}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Their wishlist */}
          <div>
            <h3 className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
              <Star size={12} />
              Wishlist ({wishlist.length})
            </h3>
            {wishlist.length === 0 ? (
              <p className="text-[13px] text-ink-secondary bg-white border border-border rounded-xl p-4">
                Their wishlist is empty.
              </p>
            ) : (
              <div className="space-y-2">
                {wishlist.map((w) => {
                  const config = getVariantConfig(w.variant);
                  return (
                    <div
                      key={`${w.cardId}|${w.variant}`}
                      className="bg-white border border-border rounded-xl p-2.5 flex items-center gap-2.5"
                    >
                      <img
                        src={w.cardImage || FALLBACK_IMG}
                        alt={w.cardName}
                        className="w-9 h-12 object-contain rounded flex-shrink-0"
                        onError={(e) => {
                          e.target.src = FALLBACK_IMG;
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold text-ink-primary truncate">
                          {w.cardName || w.cardId}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={`${config.color} text-white text-[8px] font-bold rounded px-1 py-0.5`}>
                            {config.short}
                          </span>
                          <span className="text-[10px] text-ink-secondary truncate">
                            {w.setName} #{w.cardNumber}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border flex-shrink-0 ${
                          PRIORITY_STYLES[w.priority] || PRIORITY_STYLES.medium
                        }`}
                      >
                        {w.priority.toUpperCase()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendDetailView;
